// web/src/lib/apiClient.ts — typed fetch wrapper for the Matchrail REST API.
//
// All requests go to /api/* (same origin in prod via nginx; proxied by Vite in dev).
// Tokens are read from the auth store at call time so a refresh updates every
// in-flight call without needing to thread state. On a 401, the client transparently
// attempts a refresh once, retries the original request, and signs the user out
// if the refresh fails.
import { useAuthStore } from "../stores/authStore";
const BASE = "/api";
// Build-time version stamp. Vite replaces this at build time.
// When the API returns a different version via X-App-Version header,
// the SPA reloads to pick up the new JS bundle.
const BUILD_VERSION = __BUILD_VERSION__;
let _versionMismatchReloading = false;
export class ApiError extends Error {
    status;
    body;
    constructor(status, body, message) {
        super(message);
        this.status = status;
        this.body = body;
    }
}
// Render an ApiError into a human-readable string. FastAPI 422 bodies are
// `{detail: [{loc, msg, type}, ...]}` — flatten the msg fields. Other JSON
// bodies fall back to the detail string or a JSON dump; plain text bodies
// are returned as-is.
export function formatApiError(err) {
    const body = err.body;
    if (body && typeof body === "object" && "detail" in body) {
        const detail = body.detail;
        if (Array.isArray(detail)) {
            return `${err.status}: ${detail
                .map((d) => d && typeof d === "object" && "msg" in d
                ? String(d.msg)
                : JSON.stringify(d))
                .join("; ")}`;
        }
        if (typeof detail === "string")
            return `${err.status}: ${detail}`;
    }
    if (typeof body === "string")
        return `${err.status}: ${body}`;
    return `${err.status}: ${JSON.stringify(body)}`;
}
async function rawRequest(path, opts = {}) {
    const { method = "GET", body, auth = true } = opts;
    const headers = { "Content-Type": "application/json" };
    if (auth) {
        const token = useAuthStore.getState().accessToken;
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let parsed = null;
    const text = await res.text();
    if (text) {
        try {
            parsed = JSON.parse(text);
        }
        catch {
            parsed = text;
        }
    }
    if (!res.ok) {
        throw new ApiError(res.status, parsed, `${method} ${path} → ${res.status}`);
    }
    // Check if the server is running a newer build than this JS bundle.
    // If so, reload the page to pick up the new assets.
    // Skip on auth endpoints — they run before the app is loaded and would
    // cause a reload loop if the web deploy lags behind the API deploy.
    if (auth) {
        const serverVersion = res.headers.get("X-App-Version");
        // Only reload if both sides have real version strings (7-char hex SHA).
        // Skip when either side is empty, "unknown", or missing.
        if (serverVersion &&
            serverVersion.length >= 7 &&
            BUILD_VERSION.length >= 7 &&
            serverVersion !== BUILD_VERSION &&
            !_versionMismatchReloading) {
            _versionMismatchReloading = true;
            window.location.reload();
        }
    }
    return parsed;
}
export async function apiRequest(path, opts = {}) {
    try {
        return await rawRequest(path, opts);
    }
    catch (err) {
        if (err instanceof ApiError && err.status === 401 && opts.auth !== false) {
            const refreshed = await useAuthStore.getState().refresh();
            if (refreshed) {
                return await rawRequest(path, opts);
            }
            useAuthStore.getState().logout();
        }
        throw err;
    }
}
export const api = {
    signup: (email, password, mode = "real") => apiRequest("/auth/signup", { method: "POST", body: { email, password, mode }, auth: false }),
    listPersonas: () => apiRequest("/profile/personas", { auth: false }),
    loadPersona: (persona) => apiRequest("/profile/me/load_persona", {
        method: "POST",
        body: { persona },
    }),
    generatePersona: (description) => apiRequest("/profile/me/generate_persona", {
        method: "POST",
        body: { description },
    }),
    login: (email, password) => apiRequest("/auth/login", { method: "POST", body: { email, password }, auth: false }),
    refreshToken: (refresh_token) => apiRequest("/auth/refresh", { method: "POST", body: { refresh_token }, auth: false }),
    me: () => apiRequest("/profile/me"),
    xray: (regenerate = false) => apiRequest(`/xray${regenerate ? "?regenerate=true" : ""}`),
    screeningBoard: (style) => apiRequest(`/screening/board${style ? `?style=${encodeURIComponent(style)}` : ""}`),
    vitals: () => apiRequest("/vitals"),
    screeningTrace: (style) => apiRequest(`/screening/trace?style=${encodeURIComponent(style)}`),
    screeningBuild: () => apiRequest("/screening/build", { method: "POST" }),
    feed: () => apiRequest("/feed"),
    shadowProfile: () => apiRequest("/profile/shadow"),
    updateProfile: (patch) => apiRequest("/profile/me", { method: "PUT", body: patch }),
    getOnboardingSteps: () => apiRequest("/profile/onboarding/steps"),
    completeOnboarding: (patch) => apiRequest("/profile/onboarding/complete", {
        method: "POST",
        body: patch,
    }),
    execute: (user_message) => apiRequest("/execute", { method: "POST", body: { user_message } }),
    executeStatus: (task_id) => apiRequest(`/execute/${task_id}`),
    executeResult: (task_id) => apiRequest(`/execute/${task_id}/result`),
    executeStop: (task_id) => apiRequest(`/execute/${task_id}`, { method: "DELETE" }),
    // Conversation history
    getRecentConversations: (limit = 3, offset = 0) => apiRequest(`/conversations/recent?limit=${limit}&offset=${offset}`),
    getConversationMessages: (conversationId) => apiRequest(`/conversations/${conversationId}/messages`),
    // Agent roster (system)
    getAgents: () => apiRequest("/agents"),
    // Custom user agents
    createUserAgent: (body) => apiRequest("/agents/user", { method: "POST", body }),
    listUserAgents: () => apiRequest("/agents/user"),
    updateUserAgent: (name, body) => apiRequest(`/agents/user/${encodeURIComponent(name)}`, { method: "PATCH", body }),
    deleteUserAgent: (name) => apiRequest(`/agents/user/${encodeURIComponent(name)}`, { method: "DELETE" }),
    // Inbox / pending insights
    getPendingInsights: () => apiRequest("/profile/pending-insights"),
    approveInsight: (id) => apiRequest(`/profile/pending-insights/${id}/approve`, { method: "POST" }),
    rejectInsight: (id) => apiRequest(`/profile/pending-insights/${id}/reject`, { method: "POST" }),
    // Advisory insights — stored for future use
    getInsights: () => apiRequest("/insights"),
    acknowledgeInsight: (id) => apiRequest(`/insights/${id}/acknowledge`, { method: "POST" }),
    // Starter cards — recent suggestions shown in the Luna empty state
    getStarters: () => apiRequest("/insights/starters"),
    // Interactive cards
    submitCardResponse: (body) => apiRequest("/interactive-card/response", {
        method: "POST",
        body,
    }),
    // Response feedback
    submitFeedback: (traceId, rating, correction) => apiRequest(`/response/${traceId}/feedback`, {
        method: "POST",
        body: correction?.trim() ? { rating, correction: correction.trim() } : { rating },
    }),
    // Artifacts (pinned messages, reports, projections)
    getArtifacts: (limit = 200) => apiRequest(`/artifacts?limit=${limit}`),
    createArtifact: (payload) => apiRequest("/artifacts", { method: "POST", body: payload }),
    deleteArtifact: (id) => apiRequest(`/artifacts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    deleteConversation: (id) => apiRequest(`/conversations/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
